import React, { ReactNode } from 'react';
import { Form, FormControlProps } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import TextareaAutosize, {
  TextareaAutosizeProps,
} from 'react-textarea-autosize';
import InputError from '../../form/InputError';
import { InputWithSaveBase } from '../../form/InputsWithSave/InputWithSaveBase';
import { getError } from '../utils';

type Props = InputProps | TextAreaProps | NumberInputProps;

type InputProps = Omit<FormControlProps, 'style'> & {
  className?: string;
  containerClassName?: string;
  placeholder?: string;
  labelComponent?: ReactNode;
  value: string;
  isInEditModeInitial?: boolean;
  readOnly?: boolean;
  onChange: (value: string | number | undefined) => void;
};

type NumberInputProps = InputProps & {
  type: 'number';
  value: number | undefined;
};

type TextAreaProps = InputProps &
  TextareaAutosizeProps & {
    type: 'textarea';
  };

export const InputWithSave: React.FC<React.PropsWithChildren<Props>> = ({
  type = 'text',
  isInEditModeInitial,
  className,
  containerClassName,
  labelComponent,
  value,
  onChange,
  ...restProps
}) => (
  <InputWithSaveBase
    containerClassName={containerClassName}
    value={value}
    labelComponent={labelComponent}
    onChange={onChange}
    readOnly={restProps.readOnly}
    disabled={restProps.disabled}
    type={type}
    renderInput={(inputProps) =>
      type === 'textarea' ? (
        <TextareaAutosize
          {...restProps}
          disabled={inputProps.disabled}
          readOnly={inputProps.readOnly}
          value={inputProps.value}
          onChange={(event) => inputProps.onChange(event.target.value)}
          className={['form-control', inputProps.className].join(' ')}
        />
      ) : (
        <Form.Control
          {...restProps}
          type={type}
          disabled={inputProps.disabled}
          readOnly={inputProps.readOnly}
          value={inputProps.value}
          onChange={(event) => inputProps.onChange(event.target.value)}
        />
      )
    }
  />
);

type PropsControlled = { name: string } & Omit<Props, 'value' | 'onChange'>;

export const InputWithSaveControlled: React.FC<
  React.PropsWithChildren<PropsControlled>
> = ({ name, isInEditModeInitial, ...props }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = getError(name, errors);

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <InputWithSave
            {...props}
            value={field.value ?? ''}
            onChange={field.onChange}
            isInEditModeInitial={isInEditModeInitial || !!error}
            containerClassName={error ? 'is-invalid' : ''}
            className={error ? 'is-invalid' : ''}
          />
        )}
      />
      <InputError error={error} />
    </>
  );
};
